import { Component } from "@theme/component";
import { fetchConfig, onAnimationEnd, preloadImage } from "@theme/utilities";
import {
	ThemeEvents,
	CartAddEvent,
	CartErrorEvent,
	VariantUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { resolveFreeGiftLine } from "@theme/free-gift";
import { morph } from "@theme/morph";

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * Why an add that would exceed the product's per-order limit was refused.
 *
 * @param {number} limit
 * @param {number} inCart - How many of the product the cart already holds.
 */
function maxPerOrderMessage(limit, inCart) {
	if (inCart === 0) return `Limit ${limit} per order.`;
	if (limit === 1) return "Limit 1 per order. It's already in your cart.";
	return `Limit ${limit} per order. You already have ${inCart} in your cart.`;
}

/**
 * An AbortSignal that fires after `ms`. Stands in for AbortSignal.timeout, which iOS Safari
 * lacks before 16 and throws on, killing the add before the request goes out.
 *
 * @param {number} ms
 */
function cartRequestTimeoutSignal(ms) {
	const controller = new AbortController();
	setTimeout(() => controller.abort(), ms);
	return controller.signal;
}

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
	requiredRefs = ["addToCartButton"];

	/** @type {number | undefined} */
	#animationTimeout;

	/** @type {number | undefined} */
	#cleanupTimeout;

	connectedCallback() {
		super.connectedCallback();

		this.addEventListener("pointerenter", this.#preloadImage);
	}

	disconnectedCallback() {
		super.disconnectedCallback();

		if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
		if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
		this.removeEventListener("pointerenter", this.#preloadImage);
	}

	/**
	 * Disables the add to cart button.
	 */
	disable() {
		this.refs.addToCartButton.disabled = true;
	}

	/**
	 * Enables the add to cart button.
	 */
	enable() {
		this.refs.addToCartButton.disabled = false;
	}

	/**
	 * Handles the click event for the add to cart button.
	 * @param {MouseEvent & {target: HTMLElement}} event - The click event.
	 */
	handleClick(event) {
		this.animateAddToCart();

		if (!event.target.closest(".quick-add-modal")) this.#animateFlyToCart();
	}

	#preloadImage = () => {
		const image = this.dataset.productVariantMedia;

		if (!image) return;

		preloadImage(image);
	};

	/**
	 * Animates the fly to cart animation.
	 */
	#animateFlyToCart() {
		const { addToCartButton } = this.refs;
		const cartIcon = document.querySelector(".header-actions__cart-icon");

		const image = this.dataset.productVariantMedia;

		if (!cartIcon || !addToCartButton || !image) return;

		const flyToCartElement = /** @type {FlyToCart} */ (
			document.createElement("fly-to-cart")
		);

		flyToCartElement.style.setProperty("background-image", `url(${image})`);
		flyToCartElement.source = addToCartButton;
		flyToCartElement.destination = cartIcon;

		document.body.appendChild(flyToCartElement);
	}

	/**
	 * Animates the add to cart button.
	 */
	animateAddToCart() {
		const { addToCartButton } = this.refs;

		if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
		if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

		if (!addToCartButton.classList.contains("atc-added")) {
			addToCartButton.classList.add("atc-added");
		}

		this.#animationTimeout = setTimeout(() => {
			this.#cleanupTimeout = setTimeout(() => {
				this.refs.addToCartButton.classList.remove("atc-added");
			}, 10);
		}, ADD_TO_CART_TEXT_ANIMATION_DURATION);
	}
}

if (!customElements.get("add-to-cart-component")) {
	customElements.define("add-to-cart-component", AddToCartComponent);
}

/**
 * UpCart claims the PDP submit with stopImmediatePropagation() before handleSubmit can run, and
 * would then add the product on its own — without the gift. Returning false is its supported way of
 * standing aside. We only claim submits that earn a gift or add a product with a per-order limit (so
 * UpCart's drawer can't cover a refused add's message); every other form keeps its behaviour.
 */
const upcartDefaultShouldContinue = window.upcartShouldAddToCartContinue;

window.upcartShouldAddToCartContinue = (event) => {
	// UpCart asks on the click, not the submit, so the target is whatever was under the cursor —
	// usually a span inside the button. Walk up to the component and take its form.
	const target = event?.target;
	const component =
		target instanceof Element ? target.closest("product-form-component") : null;
	const form = component?.querySelector("form");

	if (component instanceof HTMLElement && form) {
		const formData = new FormData(form);
		const variantId = String(formData.get("id") ?? "");
		if (
			variantId &&
			(resolveFreeGiftLine(variantId, formData.get("selling_plan")) ||
				component.dataset.maxPerOrder)
		)
			return false;
	}

	return upcartDefaultShouldContinue ? upcartDefaultShouldContinue(event) : true;
};

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} addToCartErrorMessage - The message text inside addToCartTextError.
 * @property {HTMLElement | undefined} maxPerOrderLabel - The "Limit N per order" note, hidden when unlimited or unavailable.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
	requiredRefs = ["variantId", "liveRegion"];
	#abortController = new AbortController();

	/** @type {number | undefined} */
	#timeout;

	/** Set for a submit's whole run, so a double click can't race two adds past the cart checks. */
	#submitting = false;

	connectedCallback() {
		super.connectedCallback();

		const { signal } = this.#abortController;
		const target = this.closest(".shopify-section, dialog, product-card");
		target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
			signal,
		});
		target?.addEventListener(
			ThemeEvents.variantSelected,
			this.#onVariantSelected,
			{ signal }
		);
	}

	disconnectedCallback() {
		super.disconnectedCallback();

		this.#abortController.abort();
	}

	/**
	 * Handles the submit event for the product form.
	 *
	 * @param {Event} event - The submit event.
	 */
	async handleSubmit(event) {
		event.preventDefault();

		if (this.#timeout) clearTimeout(this.#timeout);

		if (
			this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute(
				"disabled"
			) === "true"
		)
			return;

		if (this.#submitting) return;

		const form = this.querySelector("form");
		if (!form) throw new Error("Product form element missing");

		const formData = new FormData(form);
		const variantId = String(formData.get("id"));
		const sellingPlanId = formData.get("selling_plan");

		this.#submitting = true;
		this.setAttribute("aria-busy", "true");
		try {
			const productLine = this.#buildLineItem(formData);

			let giftLine = resolveFreeGiftLine(variantId, sellingPlanId);
			// Captured with the submit: a product swap landing during the cart read below would
			// otherwise count the new product against the old product's limit and payload.
			const productId = this.dataset.productId;
			const maxPerOrder = this.dataset.maxPerOrder ? Number(this.dataset.maxPerOrder) : null;
			const claimedFromUpcart = Boolean(giftLine) || maxPerOrder !== null;

			// Checkout enforces the limit; this only says so sooner. Refused before posting rather
			// than trimmed after, because UpCart opens its drawer on any /cart/add it sees, which
			// would cover the message explaining why nothing was added.
			if (maxPerOrder !== null) {
				const inCart = await this.#quantityOfProductInCart(productId);
				if (inCart !== null && inCart + productLine.quantity > maxPerOrder) {
					// The button animates "Added" on click, before this submit runs.
					this.refs.addToCartButtonContainer?.refs.addToCartButton.classList.remove(
						"atc-added"
					);
					const message = maxPerOrderMessage(maxPerOrder, inCart);
					window.dispatchEvent(new CartErrorEvent(this.id, message));
					this.#showAddToCartError(message, 5000);
					return;
				}
			}

			// Shopify prepends each line of a multi-line add, so the cart ends up in reverse payload
			// order. Gift first here means the customer sees what they paid for at the top.
			let response = await this.#postCartAdd(
				giftLine ? [giftLine, productLine] : [productLine]
			);

			// A multi-line add is all-or-nothing, so an unbuyable gift variant would otherwise break
			// add-to-cart everywhere it applies. Losing the gift beats losing the sale.
			// Status 0 is our network failure: retrying without the gift wouldn't help.
			if (response.status !== undefined && response.status !== 0 && giftLine) {
				console.warn(
					"[product-form] Free gift add rejected; retrying without it.",
					response.description ?? response.message
				);
				giftLine = null;
				response = await this.#postCartAdd([productLine]);
			}

			if (response.status !== undefined)
				return this.#onAddToCartError(response, variantId);

			const addedLines = response.items;
			/** @type {Array<{key: string, quantity: number}>} */
			const corrections = [];

			// Shopify merges a repeat add into the existing line, so a subscription back above quantity
			// 1 was already in the cart. Unshifted so the paid line is corrected first.
			const addedProduct = addedLines.find((line) => String(line.id) === variantId);
			const duplicateSubscription =
				sellingPlanId && addedProduct && addedProduct.quantity > 1 ? addedProduct : null;

			if (duplicateSubscription) {
				corrections.unshift({ key: duplicateSubscription.key, quantity: 1 });
			}

			const didCorrect = await this.#applyCartCorrections(corrections);

			// Shopify rendered these before the corrections. Withholding them makes
			// cart-items-component re-render from the server rather than paint stale quantities.
			const sections = corrections.length ? undefined : response.sections;

			// cart-icon treats a product-form count as a delta.
			const requested = (giftLine ? [productLine, giftLine] : [productLine]).reduce(
				(total, line) => total + line.quantity,
				0
			);
			const trimmed = corrections.reduce((total, correction) => {
				const line = addedLines.find((added) => added.key === correction.key);
				return total + (line ? line.quantity - correction.quantity : 0);
			}, 0);

			if (duplicateSubscription) {
				const message = "This product is already in your cart.";
				window.dispatchEvent(new CartErrorEvent(this.id, message));
				this.#showAddToCartError(message, 5000);
			}

			if (!duplicateSubscription) {
				const { addToCartTextError } = this.refs;
				if (addToCartTextError) {
					addToCartTextError.classList.add("hidden");
					addToCartTextError.removeAttribute("aria-live");
				}

				const addToCartButton =
					this.refs.addToCartButtonContainer?.refs.addToCartButton;
				if (addToCartButton) {
					const addedTextElement = addToCartButton.querySelector(
						".add-to-cart-text--added"
					);
					const addedText =
						addedTextElement?.textContent?.trim() || Theme.translations.added;

					this.#setLiveRegionText(addedText);
					setTimeout(() => this.#clearLiveRegionText(), 5000);
				}
			}

			this.dispatchEvent(
				new CartAddEvent({}, variantId, {
					source: "product-form-component",
					itemCount: didCorrect ? requested - trimmed : requested,
					variantId,
					sections,
				})
			);

			// Without this the drawer UpCart stood aside for neither opens nor resyncs.
			if (claimedFromUpcart) window.upcartRegisterAddToCart?.();
		} catch (error) {
			console.error(error);
		} finally {
			this.#submitting = false;
			this.removeAttribute("aria-busy");
			cartPerformance.measureFromEvent("add:user-action", event);
		}
	}

	/**
	 * How many of the product the cart holds, summed over every line as checkout counts
	 * them (any variant, properties, selling plan, gift lines included). Null when the cart can't
	 * be read: the caller lets the add through, since checkout enforces the limit anyway.
	 *
	 * @param {string | undefined} productId
	 * @returns {Promise<number | null>}
	 */
	async #quantityOfProductInCart(productId) {
		try {
			// Bounded so a hung cart read can't hold up the add.
			const response = await fetch(`${Theme.routes.cart_url}.js`, {
				signal: cartRequestTimeoutSignal(3000),
			});
			if (!response.ok) throw new Error(response.statusText);

			/** @type {{items: CartLine[]}} */
			const cart = await response.json();

			return cart.items.reduce(
				(total, line) =>
					String(line.product_id) === productId
						? total + line.quantity
						: total,
				0
			);
		} catch (error) {
			console.warn(
				"[product-form] Could not read the cart before a limited add; letting checkout enforce it.",
				error
			);
			return null;
		}
	}

	/**
	 * Adds lines to the cart in one request.
	 *
	 * @param {CartLineInput[]} items
	 * @returns {Promise<CartAddResponse>} The parsed response, error shape included.
	 */
	#postCartAdd(items) {
		/** @type {Record<string, any>} */
		const body = { items };

		/** @type {string[]} */
		const sectionIds = [];
		document.querySelectorAll("cart-items-component").forEach((item) => {
			if (item instanceof HTMLElement && item.dataset.sectionId) {
				sectionIds.push(item.dataset.sectionId);
			}
		});
		if (sectionIds.length > 0) {
			body.sections = sectionIds.join(",");
			body.sections_url = window.location.pathname;
		}

		// Bounded, since the submit guard holds every retry until this settles. A network failure
		// comes back in the error shape so the shopper gets a message, not silence. The add may
		// still have landed (aborting doesn't undo it), so the message doesn't promise it failed.
		return fetch(Theme.routes.cart_add_url, {
			...fetchConfig("json", { body: JSON.stringify(body) }),
			signal: cartRequestTimeoutSignal(20000),
		})
			.then((response) => response.json())
			.catch((error) => {
				console.error(error);
				const message = "We couldn't confirm this was added. Please check your cart before trying again.";
				return { status: 0, message, description: message };
			});
	}

	/**
	 * @param {CartErrorBody} response - An errored /cart/add.js response.
	 * @param {string} variantId
	 */
	#onAddToCartError(response, variantId) {
		window.dispatchEvent(new CartErrorEvent(this.id, response.message));
		this.#showAddToCartError(response.message, 10000);

		this.dispatchEvent(
			new CartAddEvent({}, this.id, {
				didError: true,
				source: "product-form-component",
				itemCount: 1,
				variantId,
			})
		);
	}

	/**
	 * Turns the product form into a /cart/add.js line item.
	 *
	 * @param {FormData} formData
	 * @returns {CartLineInput}
	 */
	#buildLineItem(formData) {
		const sellingPlanId = formData.get("selling_plan");
		/** @type {Record<string, string>} */
		const properties = {};

		for (const [key, value] of formData.entries()) {
			const match = key.match(/^properties\[(.+)\]$/);
			if (match && typeof value === "string") properties[match[1]] = value;
		}

		return {
			id: Number(formData.get("id")),
			// A subscription is one-per-cart, so it never ships a quantity above 1.
			quantity: sellingPlanId ? 1 : Number(formData.get("quantity")) || 1,
			...(sellingPlanId && { selling_plan: Number(sellingPlanId) }),
			...(Object.keys(properties).length && { properties }),
		};
	}

	/**
	 * Puts cart lines back to the quantity we intended, in one request.
	 *
	 * Batched rather than one /cart/change per line so the cart is never half-corrected.
	 *
	 * @param {Array<{key: string, quantity: number}>} corrections
	 * @returns {Promise<boolean>} Whether the cart now holds the quantities we intended.
	 */
	async #applyCartCorrections(corrections) {
		if (!corrections.length) return true;

		const updates = Object.fromEntries(
			corrections.map(({ key, quantity }) => [key, quantity])
		);

		try {
			// Bounded like the add: the submit guard waits on this too.
			const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
				...fetchConfig("json", { body: JSON.stringify({ updates }) }),
				signal: cartRequestTimeoutSignal(10000),
			});
			const cart = await response.json();

			if (!response.ok || cart.status) {
				throw new Error(cart.description ?? cart.message ?? response.statusText);
			}

			return true;
		} catch (error) {
			console.error(
				"[product-form] Could not correct cart quantities; the cart may hold duplicates.",
				error
			);
			return false;
		}
	}

	/**
	 * Shows the inline add-to-cart error and schedules it to hide again.
	 *
	 * @param {string} message
	 * @param {number} hideAfterMs
	 */
	#showAddToCartError(message, hideAfterMs) {
		const { addToCartTextError, addToCartErrorMessage } = this.refs;

		if (addToCartErrorMessage) addToCartErrorMessage.textContent = message;
		addToCartTextError?.classList.remove("hidden");
		this.#setLiveRegionText(message);

		this.#timeout = setTimeout(() => {
			addToCartTextError?.classList.add("hidden");
			this.#clearLiveRegionText();
		}, hideAfterMs);
	}

	/**
	 * @param {*} text
	 */
	#setLiveRegionText(text) {
		const liveRegion = this.refs.liveRegion;
		liveRegion.textContent = text;
	}

	#clearLiveRegionText() {
		const liveRegion = this.refs.liveRegion;
		liveRegion.textContent = "";
	}

	/**
	 * Carries the new product's per-order limit onto this form after a product swap, which patches
	 * the form rather than re-rendering it. The limit is per product, so any of the new product's
	 * forms in the fetched HTML carries the right one.
	 *
	 * @param {Document | Element} html - The swapped-in product's server-rendered HTML.
	 * @param {string} productId
	 */
	#syncMaxPerOrder(html, productId) {
		const source = html.querySelector(`product-form-component[data-product-id="${productId}"]`);
		const maxPerOrder = source instanceof HTMLElement ? source.dataset.maxPerOrder : undefined;

		if (maxPerOrder) {
			this.dataset.maxPerOrder = maxPerOrder;
		} else {
			delete this.dataset.maxPerOrder;
		}

		const { maxPerOrderLabel } = this.refs;
		if (maxPerOrderLabel) maxPerOrderLabel.textContent = `Limit ${maxPerOrder} per order`;

		const quantityInput = this.querySelector('input[ref="quantityInput"]');
		if (!(quantityInput instanceof HTMLInputElement)) return;

		const nextInput = source?.querySelector('input[ref="quantityInput"]');
		const nextMax = nextInput instanceof HTMLInputElement ? nextInput.max : "";
		quantityInput.max = nextMax;

		// The submit reads this input's value directly, so clamping it is enough.
		if (nextMax && Number(quantityInput.value) > Number(nextMax)) quantityInput.value = nextMax;
	}

	/**
	 * @param {VariantUpdateEvent} event
	 */
	#onVariantUpdate = (event) => {
		if (event.detail.data.newProduct) {
			const { id } = event.detail.data.newProduct;
			this.dataset.productId = id;
			this.#syncMaxPerOrder(event.detail.data.html, id);
		} else if (event.detail.data.productId !== this.dataset.productId) {
			return;
		}

		const { variantId, addToCartButtonContainer, maxPerOrderLabel } = this.refs;
		const available = event.detail.resource?.available === true;

		if (maxPerOrderLabel) maxPerOrderLabel.hidden = !this.dataset.maxPerOrder || !available;

		const currentAddToCartButton =
			addToCartButtonContainer?.refs.addToCartButton;
		const newAddToCartButton = event.detail.data.html.querySelector(
			'[ref="addToCartButton"]'
		);

		if (!currentAddToCartButton) return;

		// Update the button state
		if (!available) {
			addToCartButtonContainer.disable();
			this.refs.acceleratedCheckoutButtonContainer?.setAttribute(
				"hidden",
				"true"
			);
		} else {
			addToCartButtonContainer.enable();
			this.refs.acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
		}

		// Update the add to cart button text and icon
		if (newAddToCartButton) {
			morph(currentAddToCartButton, newAddToCartButton);
		}

		// Update the variant ID
		variantId.value = event.detail.resource.id ?? "";

		// Set the data attribute for the add to cart button to the product variant media if it exists
		if (event.detail.resource) {
			const productVariantMedia =
				event.detail.resource.featured_media?.preview_image?.src;
			productVariantMedia &&
				addToCartButtonContainer?.setAttribute(
					"data-product-variant-media",
					productVariantMedia + "&width=100"
				);
		}
	};

	/**
	 * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
	 * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
	 */
	#onVariantSelected = () => {
		this.refs.addToCartButtonContainer?.disable();
	};
}

if (!customElements.get("product-form-component")) {
	customElements.define("product-form-component", ProductFormComponent);
}

class FlyToCart extends HTMLElement {
	/** @type {Element} */
	source;

	/** @type {Element} */
	destination;

	connectedCallback() {
		this.#animate();
	}

	#animate() {
		const rect = this.getBoundingClientRect();
		const sourceRect = this.source.getBoundingClientRect();
		const destinationRect = this.destination.getBoundingClientRect();

		//Define bezier curve points
		// Maybe add half of the size of the flying thingy to the x and y to make it center properly
		const offset = {
			x: rect.width / 2,
			y: rect.height / 2,
		};
		const startPoint = {
			x: sourceRect.left + sourceRect.width / 2 - offset.x,
			y: sourceRect.top + sourceRect.height / 2 - offset.y,
		};

		const endPoint = {
			x: destinationRect.left + destinationRect.width / 2 - offset.x,
			y: destinationRect.top + destinationRect.height / 2 - offset.y,
		};

		//Calculate the control points
		const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
		const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

		//Animation variables
		/** @type {number | null} */
		let startTime = null;
		const duration = 600; // 600ms

		this.style.opacity = "1";

		/**
		 * Animates the flying thingy along the bezier curve.
		 * @param {number} currentTime - The current time.
		 */
		const animate = (currentTime) => {
			if (!startTime) startTime = currentTime;
			const elapsed = currentTime - startTime;
			const progress = Math.min(elapsed / duration, 1);

			// Calculate current position along the bezier curve
			const position = bezierPoint(
				progress,
				startPoint,
				controlPoint1,
				controlPoint2,
				endPoint
			);

			//Update the position of the flying thingy
			this.style.setProperty("--x", `${position.x}px`);
			this.style.setProperty("--y", `${position.y}px`);

			// Scale down as it approaches the cart
			const scale = 1 - progress * 0.5;
			this.style.setProperty("--scale", `${scale}`);

			//Continue the animation if not finished
			if (progress < 1) {
				requestAnimationFrame(animate);
			} else {
				//Fade out the flying thingy
				this.style.opacity = "0";
				onAnimationEnd(this, () => this.remove());
			}
		};

		// Position the flying thingy back to the start point
		this.style.setProperty("--x", `${startPoint.x}px`);
		this.style.setProperty("--y", `${startPoint.y}px`);

		//Start the animation
		requestAnimationFrame(animate);
	}
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
	const cX = 3 * (p1.x - p0.x);
	const bX = 3 * (p2.x - p1.x) - cX;
	const aX = p3.x - p0.x - cX - bX;

	const cY = 3 * (p1.y - p0.y);
	const bY = 3 * (p2.y - p1.y) - cY;
	const aY = p3.y - p0.y - cY - bY;

	const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
	const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

	return { x, y };
}

if (!customElements.get("fly-to-cart")) {
	customElements.define("fly-to-cart", FlyToCart);
}
